// @flow
import {atom, map, batched} from 'nanostores';
import {createStoresContext} from '../../../utils/react/nanostoresContext';
import {arrayMove} from '../../../utils/array';

export type OptionFormData = {
  name: string,
  color?: string,
  imageFile?: File,
  imageId?: string,
  enabled: boolean,
  storeProductOptionId?: string,
  price?: number,
  id: string,
};

export type ProductOptionFormData = {
  name: string,
  description?: string,
  enabled: boolean,
};

export type ValidationErrors = {
  name?: string,
  description?: string,
  options?: Array<{
    name?: string,
    price?: string,
  }>,
};

export type FormState = {
  formData: ProductOptionFormData,
  options: OptionFormData[],
  errors: ValidationErrors,
  isValid: boolean,
};

const initialFormData: ProductOptionFormData = {
  name: '',
  description: '',
  enabled: true,
};

const initialOptions: Array<OptionFormData> = [
  {name: '', enabled: true, price: 0},
];

const initialErrors: ValidationErrors = {};

export const {
  StoresProvider: ProductOptionFormProvider,
  useActions,
  useStoreSelector,
} = createStoresContext<FormState>(initialState => {
  const {formData, options, errors} = initialState || {};
  const $formData = map<ProductOptionFormData>(formData || initialFormData);
  const $options = atom<OptionFormData[]>(options || initialOptions);
  const $errors = atom<ValidationErrors>(errors || initialErrors);

  const $isValid = batched(
    [$formData, $options, $errors],
    (formData, options, errors) => {
      if (errors.name) return false;
      const hasOptionsErrors = errors.options?.some(
        optionError => optionError.name || optionError.price
      );
      if (hasOptionsErrors) return false;

      if (!formData.name.trim()) return false;
      if (options.some(option => !option.name.trim())) return false;

      return true;
    }
  );

  return {
    $formData,
    $options,
    $errors,
    $isValid,
  };
});

export function useProductOptionFormActions() {
  const actions = useActions(s => ({
    /* ------ GROUP ACTIONS ------ */
    updateGroupDescription: (description: string) => {
      s.$formData.setKey('description', description);
    },
    updateGroupEnabled: (enabled: boolean) => {
      s.$formData.setKey('enabled', enabled);
    },
    updateGroupName: (name: string) => {
      s.$formData.setKey('name', name);
    },

    /* ------ OPTION ACTIONS ------ */
    addOption: () => {
      const currentOptions = s.$options.get();
      const id = crypto.randomUUID();
      const newOption = {name: '', enabled: true, price: 0, id};
      s.$options.set([...currentOptions, newOption]);
    },

    removeOption: (id: string) => {
      const currentOptions = s.$options.get();
      const newOptions = currentOptions.filter(option => option.id !== id);
      s.$options.set(newOptions);
    },

    updateOptionName: (id: string, name: string) => {
      const currentOptions = s.$options.get();
      const newOptions = currentOptions.map(option =>
        option.id === id ? {...option, name} : option
      );
      s.$options.set(newOptions);
    },

    updateOptionPrice: (id: string, price: number) => {
      const currentOptions = s.$options.get();
      const newOptions = currentOptions.map(option =>
        option.id === id ? {...option, price} : option
      );
      s.$options.set(newOptions);
    },

    updateOptionColor: (id: string, color: string) => {
      const currentOptions = s.$options.get();
      const newOptions = currentOptions.map(option =>
        option.id === id ? {...option, color} : option
      );
      s.$options.set(newOptions);
    },

    removeOptionColor: (id: string) => {
      const currentOptions = s.$options.get();
      const newOptions = currentOptions.map(option =>
        option.id === id ? {...option, color: undefined} : option
      );
      s.$options.set(newOptions);
    },

    updateOptionEnabled: (id: string, enabled: boolean) => {
      const currentOptions = s.$options.get();
      const newOptions = currentOptions.map(option =>
        option.id === id ? {...option, enabled} : option
      );
      s.$options.set(newOptions);
    },

    updateOptionImageFile: (id: string, imageFile: File) => {
      const currentOptions = s.$options.get();
      const newOptions = currentOptions.map(option =>
        option.id === id ? {...option, imageFile} : option
      );
      s.$options.set(newOptions);
    },

    updateOptionImageId: (id: string, imageId: string) => {
      const currentOptions = s.$options.get();
      const newOptions = currentOptions.map(option =>
        option.id === id ? {...option, imageId} : option
      );
      s.$options.set(newOptions);
    },

    removeOptionImage: (id: string) => {
      const currentOptions = s.$options.get();
      const newOptions = currentOptions.map(option =>
        option.id === id
          ? {...option, imageId: undefined, imageFile: undefined}
          : option
      );
      s.$options.set(newOptions);
    },

    moveOption: (fromIndex: number, toIndex: number) => {
      const currentOptions = s.$options.get();
      const newOptions = arrayMove(currentOptions, fromIndex, toIndex);
      s.$options.set(newOptions);
    },

    clearErrors: () => {
      s.$errors.set({});
    },

    validateForm: () => {
      const formData = s.$formData.get();
      const options = s.$options.get();
      const errors: ValidationErrors = {};

      if (!formData.name.trim()) {
        errors.name = 'El nombre es requerido';
      }

      const optionErrors = options
        .map(option => {
          const optionError: {name?: string, price?: string} = {};

          if (!option.name.trim()) {
            optionError.name = 'El nombre es requerido';
          }

          if (option.price && option.price < 0) {
            optionError.price = 'El precio debe ser mayor o igual a 0';
          }

          return Object.keys(optionError).length > 0 ? optionError : undefined;
        })
        .filter(Boolean);

      if (optionErrors.length > 0) {
        errors.options = optionErrors;
      }

      s.$errors.set(errors);
      return Object.keys(errors).length === 0;
    },

    resetForm: (
      newData?: ProductOptionFormData,
      newOptions?: Array<OptionFormData>
    ) => {
      s.$formData.set(newData || initialFormData);
      s.$options.set(newOptions || initialOptions);
      s.$errors.set({});
    },
  }));

  return actions;
}
